import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/Header';
import {
  MessageSquare,
  Users,
  Shield,
  FileText,
  Settings,
  BarChart3,
  Phone,
  ArrowRight,
} from 'lucide-react';

interface ModuleCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
  color: string;
  badge?: string;
}

// Static class map for hover:border-{color} to avoid dynamic Tailwind classes
const hoverBorderMap: Record<string, string> = {
  'accent-1': 'hover:border-accent-1',
  'accent-2': 'hover:border-accent-2',
  'success': 'hover:border-success',
  'warning': 'hover:border-warning',
  'info': 'hover:border-info',
  'error': 'hover:border-error',
};

const bgColorMap: Record<string, string> = {
  'accent-1': 'bg-accent-1/10 text-accent-1',
  'accent-2': 'bg-accent-2/10 text-accent-2',
  'success': 'bg-success/10 text-success',
  'warning': 'bg-warning/10 text-warning',
  'info': 'bg-info/10 text-info',
  'error': 'bg-error/10 text-error',
};

function ModuleCard({ title, description, icon, onClick, color, badge }: ModuleCardProps) {
  const hoverBorder = hoverBorderMap[color] || 'hover:border-accent-1';
  const iconBg = bgColorMap[color] || 'bg-accent-1/10 text-accent-1';

  return (
    <button
      onClick={onClick}
      className={`
        relative flex flex-col items-start p-6 rounded-xl border-2 border-bg-3
        bg-bg-2 ${hoverBorder} hover:shadow-lg transition-all duration-200
        text-left group w-full
      `}
    >
      {badge && (
        <span className="absolute top-3 right-3 px-2 py-0.5 text-xs font-medium rounded-full bg-accent-1/20 text-accent-1">
          {badge}
        </span>
      )}

      <div className={`p-3 rounded-lg ${iconBg} mb-4`}>
        {icon}
      </div>

      <h3 className="text-lg font-semibold text-text-1 mb-2">{title}</h3>
      <p className="text-sm text-text-3 mb-4">{description}</p>

      <div className="flex items-center text-sm font-medium text-accent-1 group-hover:gap-2 transition-all">
        Acessar
        <ArrowRight size={16} className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </button>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <div className="min-h-screen bg-bg-1">
      <Header
        title="Painel Administrativo"
        subtitle="Selecione um módulo para começar"
      />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-accent-1/10 to-accent-2/10 rounded-xl p-6 mb-8 border border-accent-1/20">
          <h2 className="text-xl font-bold text-text-1 mb-2">
            Bem-vindo, {user?.name}!
          </h2>
          <p className="text-text-2">
            Como administrador, você tem acesso aos módulos de gestão abaixo.
            Selecione uma opção para continuar.
          </p>
        </div>

        {/* Modules Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* WABA / Disparos */}
          <ModuleCard
            title="Central de Disparos"
            description="Acesse suas contas WABA, gerencie templates e execute campanhas de disparo em massa."
            icon={<MessageSquare size={24} />}
            onClick={() => navigate('/waba')}
            color="accent-1"
            badge="Principal"
          />

          {/* User Management */}
          <ModuleCard
            title="Gestão de Usuários"
            description="Visualize, crie e gerencie usuários do sistema. Configure permissões e acessos."
            icon={<Users size={24} />}
            onClick={() => navigate('/admin/users')}
            color="success"
          />

          {/* WABA Access Control */}
          <ModuleCard
            title="Controle de Acesso WABA"
            description="Configure quais usuários podem acessar cada conta WABA e defina limites de disparo."
            icon={<Shield size={24} />}
            onClick={() => navigate('/admin/waba-access')}
            color="warning"
          />

          {/* Audit Logs */}
          <ModuleCard
            title="Logs de Auditoria"
            description="Monitore todas as ações realizadas no sistema. Rastreie atividades e alterações."
            icon={<FileText size={24} />}
            onClick={() => navigate('/admin/audit-logs')}
            color="info"
          />

          {/* Painel de Operações (unificado) */}
          <ModuleCard
            title="Painel de Operações"
            description="Métricas, custos, qualidade, templates, erros e faturamento em uma visão unificada."
            icon={<BarChart3 size={24} />}
            onClick={() => navigate('/admin/operations')}
            color="accent-2"
          />

          {/* WABA Accounts Management */}
          {isSuperAdmin && (
            <ModuleCard
              title="Gerenciar WABAs"
              description="Adicione, configure e gerencie contas WABA Business. Conecte novos números."
              icon={<Phone size={24} />}
              onClick={() => navigate('/admin/waba')}
              color="error"
              badge="Super Admin"
            />
          )}

          {/* Super Admin Panel */}
          {isSuperAdmin && (
            <ModuleCard
              title="Painel Super Admin"
              description="Acesso completo ao sistema. Gerencie tenants, configurações globais e muito mais."
              icon={<Settings size={24} />}
              onClick={() => navigate('/admin')}
              color="error"
              badge="Super Admin"
            />
          )}
        </div>

        {/* Quick Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-bg-2 rounded-lg p-4 border border-bg-3">
            <p className="text-xs text-text-3 uppercase tracking-wider mb-1">Seu Papel</p>
            <p className="text-lg font-semibold text-text-1 capitalize">
              {user?.role?.replace('_', ' ')}
            </p>
          </div>
          <div className="bg-bg-2 rounded-lg p-4 border border-bg-3">
            <p className="text-xs text-text-3 uppercase tracking-wider mb-1">Tenant</p>
            <p className="text-lg font-semibold text-text-1 truncate">
              {user?.tenantId ? 'Configurado' : 'N/A'}
            </p>
          </div>
          <div className="bg-bg-2 rounded-lg p-4 border border-bg-3">
            <p className="text-xs text-text-3 uppercase tracking-wider mb-1">Status</p>
            <p className="text-lg font-semibold text-success">Ativo</p>
          </div>
          <div className="bg-bg-2 rounded-lg p-4 border border-bg-3">
            <p className="text-xs text-text-3 uppercase tracking-wider mb-1">Último Acesso</p>
            <p className="text-lg font-semibold text-text-1">Agora</p>
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-8 bg-bg-2 rounded-xl p-6 border border-bg-3">
          <h3 className="text-lg font-semibold text-text-1 mb-3">Precisa de ajuda?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="font-medium text-text-2 mb-1">Central de Disparos</p>
              <p className="text-text-3">
                Para enviar mensagens, acesse a Central de Disparos, escolha uma conta WABA e execute um template.
              </p>
            </div>
            <div>
              <p className="font-medium text-text-2 mb-1">Controle de Acesso</p>
              <p className="text-text-3">
                Configure quem pode usar cada WABA e defina limites diários de disparo por usuário.
              </p>
            </div>
            <div>
              <p className="font-medium text-text-2 mb-1">Auditoria</p>
              <p className="text-text-3">
                Todas as ações são registradas. Use os logs para rastrear atividades no sistema.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
